import React, { useRef, useEffect, useState } from 'react';

import { LOCALE_STORAGE_KEY } from 'components/Locale';
import { MarketBannersProvider } from 'components/ContextBanners';
import { AuthProvider } from 'components/ContextAuth';
import { ModalsProvider } from 'components/ContextModals';
import { LoaderFixed } from 'components/Loaders';
import { Text } from 'components/Text';
import { StoreContext } from 'store';
import { getPublicIdFromLocalStorage } from 'helpers';
import { LOCALES, Localisation, Locale } from 'localisation';

import { getGeoInfo, getAssetsData, getLocalisation } from './helpers';
import { initAmplitude, setUserForTracking } from 'analytics/amplitude';
import { initOpenReplay } from 'analytics/openreplay';

import 'fonts/Panzerdog/stylesheet.scss';

import 'styles/colors.css';
import 'styles/index.css';

import './App.scss';

const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY) as Locale | undefined;

const LOCALISATION_URL = '/localization.json';
const ASSETS_URL = '/data.json';

let activated = false;
if (!activated) {
  activated = true;

  const publicId = getPublicIdFromLocalStorage();

  initAmplitude();
  setUserForTracking(publicId);
  initOpenReplay(publicId);
}

interface AppWrapperProps {
  children: React.ReactNode;
}

export const AppWrapper: React.FC<AppWrapperProps> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [locale, setLocale] = useState(savedLocale || LOCALES.ENG);
  const [localisation, setLocalisation] = useState<Localisation | null>(null);
  const [assetsData, setAssetsData] = useState(null);
  const [country, setCountry] = useState<string | null>(null);
  const [translation, setTranslation] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageError, setPageError] = useState<null | string>(null);

  const isLocalisationLoaded = useRef(false);
  const isAssetsLoaded = useRef(false);

  const fetchGeoInfo = async () => {
    try {
      await getGeoInfo(country, setCountry, locale);
    } catch (e) {
      console.error('Error fetching geo info:', e);
    }
  };

  const fetchLocalisation = async () => {
    try {
      const siteLocalisation = await getLocalisation(LOCALISATION_URL);
      setLocalisation(siteLocalisation);
      isLocalisationLoaded.current = true;

      return siteLocalisation;
    } catch (e) {
      console.error('Error fetching localisation:', e);
    }
  };

  const fetchAssetsData = async () => {
    try {
      await getAssetsData(ASSETS_URL, setAssetsData);
      isAssetsLoaded.current = true;
    } catch (e) {
      console.error('Error fetching assets data:', e);
    }
  };

  useEffect(() => {
    if (ready) {
      return;
    }

    const prepareStoreInfo = async () => {
      const siteLocalisation = await fetchLocalisation();
      if (siteLocalisation) {
        const translation = siteLocalisation[locale];
        setTranslation(translation);
      }
      await fetchAssetsData();
      if (isLocalisationLoaded.current && isAssetsLoaded.current) {
        setReady(true);
      } else {
        // message translation is initialized here because the localization may not load
        const errorMessage =
          locale === LOCALES.ENG
            ? 'Sorry, something went wrong. \nPlease try again later.'
            : 'Извини, что-то пошло не так. \nПожалуйста, попробуй позже.';
        setPageError(errorMessage);
      }
      // let render page before geo info
      fetchGeoInfo();
    };

    prepareStoreInfo().catch(e => console.error('Error in useEffect:', e));
  }, []);

  useEffect(() => {
    if (!localisation) return;

    const translation = localisation[locale];
    setTranslation(translation);
  }, [locale, localisation]);

  const enableLoadingOverlay = () => {
    document.body.style.width = '100vw';
    document.body.style.height = '100vh';
    document.body.style.overflow = 'hidden';
  };
  const disableLoadingOverlay = () => {
    document.body.style.width = 'initial';
    document.body.style.height = 'initial';
    document.body.style.overflow = 'visible';
  };

  const showLoader = () => {
    setIsLoading(true);
    enableLoadingOverlay();
  };

  const hideLoader = () => {
    setIsLoading(false);
    disableLoadingOverlay();
  };

  return (
    <StoreContext.Provider
      value={{
        localisation: [locale, setLocale],
        location: [country, setCountry],
        assetsData,
        translation,
        overlay: [enableLoadingOverlay, disableLoadingOverlay],
        loader: [showLoader, hideLoader],
      }}
    >
      <AuthProvider>
        <MarketBannersProvider>
          <ModalsProvider>
            {ready && !pageError && !isLoading ? children : null}
          </ModalsProvider>
          {isLoading && <LoaderFixed className="app-loader-icon" />}
          {pageError && (
            <div className="app-error">
              <Text.HeaderXl>Oops!</Text.HeaderXl>
              <Text.HeaderM>{pageError}</Text.HeaderM>
            </div>
          )}
        </MarketBannersProvider>
      </AuthProvider>
    </StoreContext.Provider>
  );
};
